import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  inject,
} from '@angular/core';

import { UiStore } from '@23degrees/stores/ui/ui.store';
import { DragDrop } from '@angular/cdk/drag-drop';
import { FlexibleConnectedPositionStrategy, GlobalPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, DomPortal, TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { NavigationStart, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, Subscription, fromEvent } from 'rxjs';
import { filter, skip, take, takeUntil } from 'rxjs/operators';
import { OverlayPaneAnimation } from './modal-overlay.animation';
import { ModalOverlayService } from './modal-overlay.service';

@Component({
    selector: 'modal-overlay',
    templateUrl: './modal-overlay.component.html',
    styleUrls: ['./modal-overlay.component.scss'],
    animations: [OverlayPaneAnimation],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgStyle, NgClass, NgTemplateOutlet, MatButton, TranslateModule]
})
export class ModalOverlayComponent implements OnInit {
  private document = inject(DOCUMENT);
  private modalOverlayService = inject(ModalOverlayService);
  overlay = inject(Overlay);
  viewContainerRef = inject(ViewContainerRef);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  elem = inject(ElementRef);
  private uiStore = inject(UiStore);
  private renderer = inject(Renderer2);
  private dragDropService = inject(DragDrop);

  @HostBinding('class.loading') loading: boolean = false;

  @ViewChild('template', { static: true }) template: TemplateRef<any>;
  @ViewChild('contentTemplate', { static: true }) cTemplate: TemplateRef<any>;
  @ViewChild('htmlContainer', { static: true }) htmlContainer: TemplateRef<any>;
  @ViewChild('modal') modal: ElementRef;
  // @ViewChild('gradient',{static: false }) set infoSection(elem: ElementRef){
  //   if(elem){
  //     this._infoContent =  elem
  //   }
  //   else{
  //     this._infoContent = undefined;
  //   }
  // }
  // _infoContent;

  // get hasInfoContent(){
  //   return !!(this._infoContent && this._infoContent.nativeElement.innerHTML.trim());
  // }

  /**
   * Global Options
   */
  @Input() identifier: string;
  @Input() closeOnRouteChanges: boolean = true;
  @Input() withBackdrop: boolean = true;
  @Input() withBluring: boolean = true;
  @Input() autoClose: boolean = false;
  @Input() autoCloseDelay: number = 0;
  @Input() scrollStrategy: 'close' | 'reposition' | 'block' = 'block';
  @Input() maxWidth: string;
  @Input() maxHeight: string;
  @Input() minHeight: string;
  @Input() height: string;
  @Input() blockClosing: boolean = false;
  @Input() fullSize: boolean = false;
  @Input() fullScreen: boolean = false;
  @Input() noLimits: boolean = false;
  @Input() mobileFullSize: boolean = false;
  @Input() autoSize: boolean = false;
  @Input() mediumSize: boolean = false;
  @Input() offsetPosition: boolean = false;
  @Input() preventAutoCloseOnOpen: boolean = false;
  @Input() overlayRelative: boolean = false;
  @Input() draggable: boolean = false;
  @Input() submitButtonClasses: string = '';
  @Input() submitOnEnter: boolean = true;

  @Input() cleanTemplate: boolean = false;
  @Input() contentTemplate: TemplateRef<any>;
  @Input() element: ElementRef | HTMLElement;
  @Input() portal: ComponentPortal<any>;
  @Input() renderComponent: any;
  @Input() originPosition: any[] = ['end', 'bottom'];
  @Input() overlayPosition: any[] = ['center', 'top'];
  @Input() fallbackPositionOrigin: any[];
  @Input() fallbackPositionOverlay: any[];
  @Input() preventClosingOnElementClick: boolean = true;

  @Output() isOpen: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Template Specific
   */
  @Input() title: string;
  @Input() icon: string;
  @Input() submitButtonIcon: string;
  @Input() bodyText: string;
  @Input() submitButton: string = 'Ok';
  @Input() disabled: boolean = false;
  @Input() cancelButton: string = 'applicationUI.modalClose';
  @Input() hideCloseButton: boolean = false;
  @Input() hideSubmitButton: boolean = false;
  @Input() showButtons: boolean = true;
  @Input() headerStyle: any;
  @Input() submitButtonStyle: any;

  @Input() submitButtonUrl: string;
  @Input() closeOnSubmit: boolean = false;
  @Input() noPadding: boolean = false;
  @Input() hideOverflow: boolean = false;
  @Input() hideHeader: boolean = false;
  @Input() shrinkHeader: boolean = false;
  @Input() absoluteHeader: boolean = false;
  @Input() withShadow: boolean = true;
  @Input() bgColor: string = 'var(--tt-bg-card)';
  @Input() shadowed: boolean = false;
  @Input() clearBody: boolean = false;
  @Input() infoMaxWidth: string = 'initial';
  @Input() infoWidth: string = 'auto';
  @Input() widthInfoShadow: boolean = false;
  @Input() forceTitle: boolean = false;
  @Input() transparentBackdrop: boolean = false;
  @Input() positionCoordinates: { x: string; y: string };
  @Input() domElement: HTMLElement;

  @Output() cancelClick = new EventEmitter();
  @Output() submitClick = new EventEmitter();
  @Output() startOpen = new EventEmitter();

  @Input() htmlContent: string;

  public animationState = 'hide';

  public context: any;

  overlayRef: OverlayRef | null;
  componentRef: ComponentRef<any>;
  sub: Subscription;
  hasOpened: boolean = false;
  startOpening: boolean = false;
  overlayOpen: boolean = false;

  positionStrategy: GlobalPositionStrategy | FlexibleConnectedPositionStrategy;

  closed$ = new Subject();

  constructor() {
    if (this.closeOnRouteChanges) {
      this.router.events.forEach((event) => {
        if (event instanceof NavigationStart) {
          this.closeModal();
        }
      });
    }
  }

  ngOnInit(): void {
    // this.modalOverlayService.addModal({ id: this.identifier, modal: this });
    // console.log("[Modal] Init id: ",this.identifier);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const startTime = performance.now();
    if (changes.identifier?.previousValue) {
      this.modalOverlayService.removeModal(changes.identifier.previousValue);
    }

    this.modalOverlayService.addModal({ id: this.identifier, modal: this });

    if (changes.maxHeight || changes.maxWidth || changes.height || changes.minHeight) {
      this.applyStyles();
    }

    const endTime = performance.now();
    // console.log(`modal ngOnChanges took ${endTime - startTime} ms`, changes);
  }

  ngOnDestroy(): void {
    this.closeModal();
    this.modalOverlayService.removeModal(this.identifier);
  }

  public safeDetectChanges = () => {
    if (!this.cdr['destroyed']) this.cdr.detectChanges();
  };

  public slowDetectChanges = () => {
    setTimeout(() => this.safeDetectChanges());
  };

  submit() {
    this.submitClick.emit();
    if (this.closeOnSubmit) this.closeModal();
  }

  open() {
    this.openModal();
  }

  openModal() {
    if (this.overlayRef && !this.preventAutoCloseOnOpen) {
      this.closeModal();
    }

    if (this.withBluring) {
      // this.uiService.closeMenu();
      this.uiStore.backgroundBlur();
    }

    this.startOpening = true;
    this.startOpen.emit(true);

    if (this.element) {
      if (this.fallbackPositionOrigin || this.fallbackPositionOverlay) {
        this.positionStrategy = this.overlay
          .position()
          .flexibleConnectedTo(
            this.element,
            // {
            //   originX: this.originPosition[0],
            //   originY: this.originPosition[1],
            // },
            // { overlayX: this.overlayPosition[0], overlayY: this.overlayPosition[1] }
          )
          .withPush(false)
          .withPositions([
            {
              originX: this.originPosition[0],
              originY: this.originPosition[1],
              overlayX: this.overlayPosition[0],
              overlayY: this.overlayPosition[1],
            },
            {
              originX: this.fallbackPositionOrigin[0],
              originY: this.fallbackPositionOrigin[1],
              overlayX: this.fallbackPositionOverlay[0],
              overlayY: this.fallbackPositionOverlay[1],
            },
            {
              originX: 'center',
              originY: 'top',
              overlayX: 'center',
              overlayY: 'bottom',
            },
          ]);
        // .withFallbackPosition(
        //   {
        //     originX: this.fallbackPositionOrigin[0],
        //     originY: this.fallbackPositionOrigin[1],
        //   },
        //   {
        //     overlayX: this.fallbackPositionOverlay[0],
        //     overlayY: this.fallbackPositionOverlay[1],
        //   }
        // );

        this.positionStrategy.positionChanges.subscribe((info) => {
          this.overlayRef.addPanelClass(`position-x-${info.connectionPair.overlayX}`);
          this.overlayRef.addPanelClass(`position-y-${info.connectionPair.overlayY}`);

          // console.log('position-x-' + info.connectionPair.overlayX, 'position-y-' + info.connectionPair.overlayY)
        });
      } else {
        this.positionStrategy = this.overlay
          .position()
          .flexibleConnectedTo(this.element)
          .withPositions([
            {
              originX: this.originPosition[0],
              originY: this.originPosition[1],
              overlayX: this.overlayPosition[0],
              overlayY: this.overlayPosition[1],
            },
          ])
          .withGrowAfterOpen(true)
          .withFlexibleDimensions(true);
      }
    } else if (this.positionCoordinates) {
      this.positionStrategy = this.overlay.position().global().left(this.positionCoordinates.x).top(this.positionCoordinates.y);
    } else {
      this.positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();
    }

    const classes = ['modal-overlay-pane'];
    let scrollStrategy: any = this.overlay.scrollStrategies.block();
    if (this.scrollStrategy !== 'block') {
      scrollStrategy = this.scrollStrategy === 'reposition' ? this.overlay.scrollStrategies.reposition() : this.overlay.scrollStrategies.close();
    }

    if (this.fullSize) classes.push('full-size');
    if (this.fullScreen) classes.push('full-screen');
    if (this.noLimits) classes.push('no-limits');
    if (this.autoSize) classes.push('auto-size');
    if (this.cleanTemplate) classes.push('center');
    if (this.mediumSize) classes.push('medium-size');
    if (!this.withBackdrop && this.withShadow) classes.push('with-shadow');
    if (this.clearBody) classes.push('clear-body');
    if (this.mobileFullSize) classes.push('mobile-fullsize');
    if (this.offsetPosition) classes.push('offset-position');
    if (this.overlayRelative) classes.push('relative-important');

    this.overlayRef = this.overlay.create({
      hasBackdrop: this.withBackdrop,
      positionStrategy: this.positionStrategy,
      scrollStrategy,
      panelClass: classes,
      backdropClass: this.transparentBackdrop ? 'backdrop-transparent' : '',
    });

    this.overlayRef
      .attachments()
      .pipe(take(1))
      .subscribe(() => {
        if (!this.element) {
          this.document.body.classList.add('modal-overlay-open');
        }

        this.isOpen.emit(true);
        this.overlayOpen = true;
        this.applyStyles();
        if (this.autoClose) {
          this.sub = fromEvent<MouseEvent>(document, 'click')
            .pipe(
              skip(this.autoCloseDelay),
              filter((event) => {
                if (!this.autoClose) return;
                const clickTarget = event.target as HTMLElement;
                let connectedElementClick = false;
                if (this.element && this.preventClosingOnElementClick) {
                  const elem: HTMLElement = this.element instanceof ElementRef ? this.element.nativeElement : this.element;
                  connectedElementClick = elem.contains(clickTarget);
                }
                return !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget) && !connectedElementClick;
              }),

              take(1),
            )
            .subscribe(() => this.closeModal());
        }

        if (this.draggable) {
          const drag = this.dragDropService.createDrag(this.overlayRef.hostElement);
          // drag.withBoundaryElement(this.overlayRef.hostElement.parentElement)

          // PREVENT DRAGGING ON INPUT ELEMENTS
          drag.getRootElement().onmousemove = (moveEvent: any) => {
            drag.disabled = moveEvent.target.nodeName === 'INPUT' || moveEvent.target.nodeName === 'TEXTAREA';
          };
          // ACTIVATE THESE TO JUST DRAG ON HANDLES
          // everything with class handle will work
          // const handles = [];
          // this.overlayRef.hostElement.querySelectorAll('.handle').forEach((element) => handles.push(element));
          // drag.withHandles(handles);
        }
      });

    this.overlayRef
      .detachments()
      .pipe(takeUntil(this.closed$))
      .subscribe((data) => {
        // console.log('[modalOverlayComponent]', { data });
        if (!this.element) {
          this.document.body.classList.remove(`modal-overlay-open`);
        }
        this.isOpen.emit(false);
        this.overlayOpen = false;
        this.startOpening = false;
        this.startOpen.emit(false);
        this.closed$.next(null);
        // TODO: @manfred needs to test this further
        // this.overlayRef.dispose();
      });

    if (this.domElement) {
      this.overlayRef.attach(new DomPortal(this.domElement));
    } else if (this.htmlContent) {
      this.overlayRef.attach(new TemplatePortal(this.htmlContainer, this.viewContainerRef));
    } else if (this.portal) {
      this.overlayRef.attach(this.portal);
    } else if (this.renderComponent) {
      this.componentRef = this.overlayRef.attach(new ComponentPortal(this.renderComponent, this.viewContainerRef));
    } else {
      this.overlayRef.attach(new TemplatePortal(this.contentTemplate || (this.cleanTemplate ? this.cTemplate : this.template), this.viewContainerRef));
    }

    if (this.withBackdrop) {
      this.overlayRef
        .backdropClick()
        .pipe(take(1))
        .subscribe(() => {
          if (!this.blockClosing) {
            this.closeModal();
          }
        });
    }

    this.overlayRef
      .keydownEvents()
      .pipe(takeUntil(this.closed$))
      .subscribe((event) => {
        if (event.key === 'Escape') {
          if (!this.blockClosing) {
            if (!this.contentTemplate && !this.renderComponent) {
              this.close();
            } else {
              this.closeModal();
            }
          }
        } else if (this.submitOnEnter && event.key === 'Enter') {
          this.submit();
        }
      });

    this.animationState = 'show';

    //  this.cdr.detectChanges();
  }

  close() {
    this.cancelClick.emit();
    this.animationState = 'hide';
    this.safeDetectChanges();
    if (this.withBluring) {
      const otherBlurOpen = this.modalOverlayService.modals.some((m) => m.modal.overlayOpen && m.id !== this.identifier && m.modal.withBluring);
      if (!otherBlurOpen) {
        this.uiStore.backgroundClear();
      }
    }
  }

  public applyStyles() {
    if (this.overlayRef) {
      if (this.maxWidth) {
        this.renderer.setStyle(this.overlayRef.overlayElement, 'max-width', this.maxWidth);
      }
      if (this.maxHeight) {
        this.renderer.setStyle(this.overlayRef.overlayElement, 'max-height', this.maxHeight);
      }
      if (this.height) {
        this.renderer.setStyle(this.overlayRef.overlayElement, 'height', this.height);
      }
      if (this.minHeight) {
        this.renderer.setStyle(this.overlayRef.overlayElement, 'min-height', this.minHeight);
      } else {
        this.renderer.removeStyle(this.overlayRef.overlayElement, 'min-height');
      }
    }
  }

  public closeModal() {
    if (this.sub) this.sub.unsubscribe();
    if (this.overlayRef) {
      this.animationState = 'hide';
      if (this.withBluring) {
        const otherBlurOpen = this.modalOverlayService.modals.some((m) => m.modal.overlayOpen && m.id !== this.identifier && m.modal.withBluring);
        if (!otherBlurOpen) {
          this.uiStore.backgroundClear();
        }
      }

      this.overlayRef.detach();
      this.overlayRef = null;
      this.overlayOpen = false;
    }
  }

  public animationDone(event) {
    if (event.fromState === 'show') {
      this.closeModal();
      this.hasOpened = false;
    }
    if (event.fromState === 'hide') {
      this.hasOpened = true;
    }
  }
}
